import { useState, useEffect } from 'react';
import { portalMe, portalUpdateMe, portalChangePassword } from '../services/api';
import { User, EnvelopeSimple, Phone, Users, Key, FloppyDisk, Check } from '@phosphor-icons/react';

export default function TenantProfile() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', personen_anzahl: 1 });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Password change
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwError, setPwError] = useState('');

  useEffect(() => {
    portalMe().then(res => {
      setProfile(res.data);
      setForm({
        name: res.data.name || '',
        phone: res.data.phone || '',
        personen_anzahl: res.data.personen_anzahl || 1
      });
    }).catch(console.error);
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(''); setSuccess('');
    try {
      await portalUpdateMe(form);
      setSuccess('Profil aktualisiert');
      const res = await portalMe();
      setProfile(res.data);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handlePwSubmit = async (e) => {
    e.preventDefault();
    setPwError(''); setPwSuccess('');
    if (pwForm.new_password !== pwForm.confirm) {
      setPwError('Passwörter stimmen nicht überein');
      return;
    }
    if (pwForm.new_password.length < 6) {
      setPwError('Mindestens 6 Zeichen');
      return;
    }
    setPwSaving(true);
    try {
      await portalChangePassword({ current_password: pwForm.current_password, new_password: pwForm.new_password });
      setPwSuccess('Passwort geändert');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
      setTimeout(() => setPwSuccess(''), 3000);
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Fehler');
    } finally {
      setPwSaving(false);
    }
  };

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="tenant-profile-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
          Mein Profil
        </h1>
        <p className="text-slate-500 mt-1 text-sm sm:text-base">Persönliche Daten und Stammdaten</p>
      </div>

      {/* Profile Form */}
      <form onSubmit={handleSave} className="bg-white border border-gray-200 p-6 space-y-5">
        <h2 className="font-heading text-lg font-bold text-slate-900">Stammdaten</h2>

        {error && <div className="bg-red-50 border border-red-200 text-red-800 p-3 text-sm">{error}</div>}
        {success && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 text-sm flex items-center gap-2">
            <Check weight="bold" className="w-4 h-4" /> {success}
          </div>
        )}

        <div>
          <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">E-Mail (nicht änderbar)</label>
          <div className="relative">
            <EnvelopeSimple className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="email"
              value={profile.email || ''}
              disabled
              className="w-full border-b border-gray-200 bg-gray-50 pl-8 py-2 outline-none text-slate-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Name</label>
          <div className="relative">
            <User className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              className="w-full border-b border-slate-300 bg-transparent pl-8 py-2 focus:border-slate-900 outline-none"
              data-testid="profile-name-input"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Telefon</label>
          <div className="relative">
            <Phone className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full border-b border-slate-300 bg-transparent pl-8 py-2 focus:border-slate-900 outline-none"
              data-testid="profile-phone-input"
              placeholder="+49 ..."
            />
          </div>
        </div>

        <div>
          <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">
            Anzahl Personen im Haushalt
          </label>
          <div className="relative">
            <Users className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="number"
              min="1"
              value={form.personen_anzahl}
              onChange={(e) => setForm(prev => ({ ...prev, personen_anzahl: parseInt(e.target.value) || 1 }))}
              className="w-full border-b border-slate-300 bg-transparent pl-8 py-2 focus:border-slate-900 outline-none"
              data-testid="profile-personen-input"
            />
          </div>
          <p className="text-xs text-slate-500 mt-1">Wichtig für die korrekte Nebenkostenabrechnung (z.B. Müll, Wasser)</p>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="bg-slate-900 text-white px-6 py-2.5 font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
          data-testid="profile-save-button"
        >
          <FloppyDisk weight="bold" className="w-4 h-4" />
          {saving ? 'Speichern...' : 'Speichern'}
        </button>
      </form>

      {/* Password Change */}
      <form onSubmit={handlePwSubmit} className="bg-white border border-gray-200 p-6 space-y-5">
        <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
          <Key weight="bold" className="w-5 h-5" /> Passwort ändern
        </h2>

        {pwError && <div className="bg-red-50 border border-red-200 text-red-800 p-3 text-sm">{pwError}</div>}
        {pwSuccess && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 text-sm flex items-center gap-2">
            <Check weight="bold" className="w-4 h-4" /> {pwSuccess}
          </div>
        )}

        <div>
          <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Aktuelles Passwort</label>
          <input
            type="password"
            value={pwForm.current_password}
            onChange={(e) => setPwForm(prev => ({ ...prev, current_password: e.target.value }))}
            className="w-full border-b border-slate-300 bg-transparent py-2 focus:border-slate-900 outline-none"
            required
            data-testid="profile-current-password"
          />
        </div>
        <div>
          <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Neues Passwort</label>
          <input
            type="password"
            value={pwForm.new_password}
            onChange={(e) => setPwForm(prev => ({ ...prev, new_password: e.target.value }))}
            className="w-full border-b border-slate-300 bg-transparent py-2 focus:border-slate-900 outline-none"
            required
            minLength={6}
            data-testid="profile-new-password"
          />
        </div>
        <div>
          <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Bestätigen</label>
          <input
            type="password"
            value={pwForm.confirm}
            onChange={(e) => setPwForm(prev => ({ ...prev, confirm: e.target.value }))}
            className="w-full border-b border-slate-300 bg-transparent py-2 focus:border-slate-900 outline-none"
            required
            minLength={6}
            data-testid="profile-confirm-password"
          />
        </div>

        <button
          type="submit"
          disabled={pwSaving}
          className="border border-slate-900 text-slate-900 px-6 py-2.5 font-medium hover:bg-slate-900 hover:text-white disabled:opacity-50"
          data-testid="profile-change-password-button"
        >
          {pwSaving ? 'Speichern...' : 'Passwort ändern'}
        </button>
      </form>
    </div>
  );
}
